import {
  Component,
  model,
  input,
  output,
  computed, signal, effect, untracked, TemplateRef
} from "@angular/core";
import {TableAction} from "../../../interfaces/table/table-action";
import {TableColumn} from "../../../interfaces/table/table-column";
import {CurrencyPipe, NgClass, NgStyle, NgTemplateOutlet} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {LoadingSpinnerComponent} from "../loading-spinner/loading-spinner.component";
import {TableColumnType} from "../../../enums/table-column-type";
import {ValueChangingContext} from "../../../interfaces/value-changing-context";
import {StopClickPropagationDirective} from "../../../directives/stop-click-propagation.directive";
import {TableData} from "../../../interfaces/table/table-data";
import {DecimalsPipe} from "../../../pipes/decimals.pipe";
import {TableSort} from "../../../interfaces/table/table-sort";
import {TableParams} from "../../../interfaces/table/table-params";
import {TagsComponent} from "../tags/tags.component";
import {LoadingPulseComponent} from "../loading-pulse/loading-pulse.component";

@Component({
  selector: 'custom-table',
  standalone: true,
  templateUrl: './custom-table.component.html',
  imports: [
    NgTemplateOutlet,
    CurrencyPipe,
    FormsModule,
    LoadingSpinnerComponent,
    NgClass,
    NgStyle,
    StopClickPropagationDirective,
    DecimalsPipe,
    TagsComponent,
    LoadingPulseComponent
  ],
  styleUrls: ['./custom-table.component.css']
})
export class CustomTableComponent<T extends Record<string, any> = any> {
  data = input.required<TableData<T>>();
  primaryDataKey = input('id');
  columns = input.required<TableColumn<T>[]>();
  actions = input<TableAction<T>[]>([]);
  lookup = input<string>('');
  usePagination = input<boolean>(true);
  serverPagination = input<boolean>(false);
  pageSizeOptions = input<number[]>([10, 30, 50, 100, 500]);
  clickableRows = input<boolean>(false);
  selectableRows = input<boolean>(false);
  customHeaderClasses = input<string[]>([]);
  customRowClasses = input<string[]>([]);
  paramsChange = output<TableParams>();
  rowClick = output<T>();
  selectingRowId = output<ValueChangingContext<string | null>>();
  pageSize = model<number>(10);
  selectedRowId = model<string | null>(null);
  loading = input<boolean>(false);
  pulseLoadingCount = input<number>(10);
  emptyDataMessage = input<string>('No data found');
  visibleHeader = input<boolean>(true);

  fullPageSizeOptions = computed(() => {
    const sizes = [...(this.pageSizeOptions() || [])];
    if (!sizes.includes(this.pageSize()))
      sizes.push(this.pageSize());
    return sizes.sort((a, b) => a - b);
  });
  filteredData = computed(() => {
    let data = this.data().data;
    data = this.applyFunctionColumns(data, this.columns());
    return this.applyLookupFilter(data, this.lookup());
  });
  sortedData = computed(() => this.applySorting(this.filteredData(), this.columns(), this.sortParams()));
  pagedData = computed(() => {
    if (this.serverPagination() || !this.usePagination())
      return this.sortedData();
    return this.applyPagination(this.sortedData(), this.currentPage(), this.pageSize());
  });

  protected readonly TableColumnType = TableColumnType;
  initialized = signal<boolean>(false);
  sortParams = signal<TableSort | null>(null);
  currentPage = model<number>(1);

  constructor() {
    effect(() => {
      this.pageSize();
      untracked(() => {
        this.currentPage.set(1);
      });
    });

    effect(() => {
      if (this.initialized()) return;
      queueMicrotask(() => this.initialized.set(true));
    });

    effect(() => {
      const currentPage = this.currentPage();
      const pageSize = this.pageSize();
      const sortParams = this.sortParams();

      untracked(() => {
        if (this.initialized())
          this.paramsChange.emit({currentPage, pageSize, sortParams});
      });
    });
  }

  sortByColumn(col: TableColumn<T>): void {
    if (!col.sortable) return;
    const sortParams = this.sortParams();

    if (!sortParams || sortParams.columnId !== col.columnId) {
      this.sortParams.set({
        columnId: col.columnId,
        direction: 'asc'
      });
      return;
    }
    if (sortParams.direction === 'asc') {
      this.sortParams.set({
        columnId: col.columnId,
        direction: 'desc'
      });
      return;
    }
    this.sortParams.set(null);
  }

  private applyLookupFilter(data: T[], lookup: string): T[] {
    if (!lookup)
      return data;

    const filterValue = lookup.toLowerCase();
    return data.filter(item => {
      return Object.values(item).some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(filterValue);
        } else if (Array.isArray(value)) {
          return value.some((v: any) => v.toString().toLowerCase().includes(filterValue));
        }
        return false;
      });
    });
  }

  private applySorting(data: T[], columns: TableColumn<T>[], sortParams: TableSort | null): T[] {
    if (!sortParams)
      return data;

    const columnDef = columns.find(c => c.columnId === sortParams.columnId);
    return [...data].sort((a, b) => {
      const valA = (a as Record<string, any>)[sortParams.columnId!];
      const valB = (b as Record<string, any>)[sortParams.columnId!];
      let comparison: number;
      if (typeof valA === 'number' && typeof valB === 'number') {
        comparison = valA - valB;
      } else if (columnDef?.type === TableColumnType.Boolean) {
        const boolA = !!valA;
        const boolB = !!valB;
        comparison = (boolA === boolB) ? 0 : boolA ? 1 : -1;
      } else if (columnDef?.type === TableColumnType.Currency) {
        const numA = this.parseCurrency(valA);
        const numB = this.parseCurrency(valB);
        comparison = numA - numB;
      } else {
        const strA = this.getComparableString(valA, columnDef?.type);
        const strB = this.getComparableString(valB, columnDef?.type);
        comparison = strA.localeCompare(strB);
      }
      if (isNaN(comparison))
        comparison = 0;
      return sortParams.direction === 'asc' ? comparison : -comparison;
    });
  }

  private applyFunctionColumns(data: T[], columns: TableColumn<T>[]): T[] {
    return data.map(row => {
      let newRow = {...row};
      columns.forEach((col) => {
        if (col.calculate != null)
          newRow[col.columnId as keyof T] = col.calculate(row);
      });
      return newRow;
    });
  }

  private parseCurrency(value: any): number {
    if (value === null || typeof value === 'undefined')
      return 0;
    if (typeof value === 'number')
      return value;

    const strValue = String(value);
    const cleanedString = strValue.replace(/[^\d.-]/g, '');
    const num = parseFloat(cleanedString);
    return isNaN(num) ? 0 : num;
  }

  private getComparableString(value: any, type?: TableColumnType): string {
    if (value == null) return '';
    switch (type) {
      case TableColumnType.Link:
        return typeof value === 'object' ? value.label : String(value);
      case TableColumnType.Tag:
        if (Array.isArray(value)) {
          return value.map(v => v.name).join(',');
        } else if (value && typeof value === 'object') {
          return value.name || String(value);
        }
        return String(value);
      default:
        return String(value).toLowerCase();
    }
  }

  private applyPagination(data: T[], currentPage: number, pageSize: number): T[] {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return data.slice(start, end);
  }

  totalItems = computed(() => {
    if (this.serverPagination()) {
      return this.data().total ?? 0;
    }
    return this.sortedData().length;
  });

  totalPages = computed(() => {
    return Math.max(Math.ceil(this.totalItems() / this.pageSize()), 1);
  });

  startIndex = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  endIndex = computed(() => {
    return Math.min(this.startIndex() + this.pageSize() - 1, this.totalItems());
  });

  prevPage(): void {
    if (this.currentPage() > 1) {
      this.currentPage.update(value => value - 1);
    }
  }

  nextPage(): void {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update(value => value + 1);
    }
  }

  onRowClick(rowId: string, row: T): void {
    if (this.selectableRows()) {
      let cancel = false;
      this.selectingRowId.emit({newValue: rowId, cancel: () => cancel = true});
      if (!cancel)
        this.selectedRowId.set(rowId);
    }
    if (this.clickableRows())
      this.rowClick.emit(row);
  }

  onActionClick(action: TableAction<T>, row: T): void {
    action.execute(row);
  }

  getActionStringValue(str: ((row: any) => string) | string | undefined, row: T): string | undefined {
    if (typeof str === 'function')
      return str(row);
    return str;
  }

  getActionBooleanValue(bool: ((row: any) => boolean) | boolean | undefined, row: T): boolean {
    if (typeof bool === 'function')
      return bool(row);
    return bool != null && bool;
  }

  isArray(value: any): boolean {
    return Array.isArray(value);
  }

  getColumnStyles(col: TableColumn<T>): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    if (col.width) {
      styles['width'] = typeof col.width === 'number' ? col.width + 'px' : col.width;
    }

    if (col.minWidth) {
      styles['min-width'] = typeof col.minWidth === 'number' ? col.minWidth + 'px' : col.minWidth;
    }

    if (col.maxWidth) {
      styles['max-width'] = typeof col.maxWidth === 'number' ? col.maxWidth + 'px' : col.maxWidth;
    }

    return styles;
  }

  isTemplateRef(value: any): value is TemplateRef<any> {
    return value instanceof TemplateRef;
  }

  hasValue(value: any): boolean {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  }
}
